import React from "react";
import { Users, MessageSquare, Layers, AppWindow, CheckCircle, Radio } from "lucide-react";
import { useClaudeCode } from "../context/ClaudeCodeContext";

const Skeleton = () => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
    <div className="h-7 bg-gray-200 rounded w-1/3" />
  </div>
);

interface CardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accentBg: string;
  accentFg: string;
}

const Card: React.FC<CardProps> = ({ label, value, icon, accentBg, accentFg }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
        {label}
      </p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
    <div
      className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-3"
      style={{ backgroundColor: accentBg, color: accentFg }}
    >
      {icon}
    </div>
  </div>
);

const StatsCards: React.FC = () => {
  const { stats, statsLoading } = useClaudeCode();

  if (statsLoading && !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} />
        ))}
      </div>
    );
  }

  const s = stats || {
    total_users: 0,
    total_sessions: 0,
    total_prompts: 0,
    total_apps: 0,
    total_deployed_apps: 0,
    total_live_apps: 0,
    total_failed_apps: 0,
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Card
        label="Total Users"
        value={s.total_users}
        icon={<Users className="h-5 w-5" />}
        accentBg="#eef2ff"
        accentFg="#4f46e5"
      />
      <Card
        label="Total Sessions"
        value={s.total_sessions}
        icon={<Layers className="h-5 w-5" />}
        accentBg="#ecfeff"
        accentFg="#0891b2"
      />
      <Card
        label="Total Prompts"
        value={s.total_prompts}
        icon={<MessageSquare className="h-5 w-5" />}
        accentBg="#f0fdf4"
        accentFg="#16a34a"
      />
      <Card
        label="Total Apps"
        value={s.total_apps}
        icon={<AppWindow className="h-5 w-5" />}
        accentBg="#fef3c7"
        accentFg="#b45309"
      />
      <Card
        label="Deployed Apps"
        value={s.total_deployed_apps}
        icon={<CheckCircle className="h-5 w-5" />}
        accentBg="#dcfce7"
        accentFg="#15803d"
      />
      <Card
        label="Live Apps"
        value={s.total_live_apps ?? 0}
        icon={<Radio className="h-5 w-5" />}
        accentBg="#ede9fe"
        accentFg="#6d28d9"
      />
    </div>
  );
};

export default StatsCards;
